import React, { forwardRef, useLayoutEffect, useRef } from 'react'

import { mergeRefs } from 'utils/ref_utils'

interface ListProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  children: React.ReactNode;
}

export const List = forwardRef<HTMLDivElement, ListProps>(({ children, ...props }, forwardedRef) => {
  const ref = useRef<HTMLDivElement>(null)
  const [overflowed, setOverflowed] = React.useState(false)

  useLayoutEffect(() => {
    const element = ref.current

    if (!element) return

    const resizeObserver = new ResizeObserver(() => {
      const { scrollWidth, clientWidth } = element

      if (scrollWidth > clientWidth) {
        setOverflowed(true)
      } else {
        setOverflowed(false)
      }
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <div className='flex grow min-w-0 relative overflow-hidden'>
      <div
        {...props}
        className='flex items-stretch grow overflow-auto rounded-lg'
        ref={mergeRefs(ref, forwardedRef)}
      >
        {children}
      </div>
      {overflowed && (
        <div className='absolute top-px bottom-px right-0 w-5 bg-gradient-to-l from-gray-800/10 to-gray-800/0 pointer-events-none z-[2]' />
      )}
    </div>
  )
})

List.displayName = 'MultistepFilters.List'
